/**
 * Credits Progress Bar
 *
 * Visual progress indicator showing how close the user is to the unlock
 * threshold for their store credits. Three visual states:
 *   - hidden:   feature disabled, user not logged in, or threshold not met
 *   - progress: cart subtotal below threshold — shows fill bar + remaining
 *   - unlocked: cart subtotal >= threshold — credits ready to use at checkout
 */

'use client';

import { formatPrice, type FormatPriceOptions } from '@template-project/afrodita-components';
import { useTranslation } from '@/lib/i18n/client';
import {
  ProgressBarContainer,
  ProgressBarTrack,
  ProgressBarFill,
  ProgressBarLabels,
  ProgressBarLabel,
  ProgressBarMessage,
  ProgressBarMessageBold,
} from './CartSummaryV2.styled';
import type { CreditsProgressState } from '@/lib/hooks/use-credits';

export interface CreditsProgressBarProps {
  credits: CreditsProgressState;
  priceOptions: FormatPriceOptions;
}

export function CreditsProgressBar({
  credits,
  priceOptions,
}: Readonly<CreditsProgressBarProps>) {
  const { t } = useTranslation('sidecart');

  if (!credits.isEnabled) {
    return null;
  }

  const currentFormatted = formatPrice(
    credits.unlockThreshold - credits.remaining,
    priceOptions,
  );
  const thresholdFormatted = formatPrice(credits.unlockThreshold, priceOptions);
  const remainingFormatted = formatPrice(credits.remaining, priceOptions);
  // 1 credit = $1.00 — display as dollar amount
  const balanceFormatted = formatPrice(credits.balance * 100, priceOptions);

  if (credits.isUnlocked) {
    return (
      <ProgressBarContainer data-testid="credits-bar">
        <ProgressBarTrack>
          <ProgressBarFill $percent={100} $unlocked />
        </ProgressBarTrack>
        <ProgressBarLabels>
          <ProgressBarLabel>{currentFormatted}</ProgressBarLabel>
          <ProgressBarLabel>{thresholdFormatted}</ProgressBarLabel>
        </ProgressBarLabels>
        <ProgressBarMessage
          dangerouslySetInnerHTML={{
            __html: t('cart.credits.unlocked', { balance: balanceFormatted }),
          }}
          data-testid="credits-bar-unlocked"
        />
      </ProgressBarContainer>
    );
  }

  return (
    <ProgressBarContainer data-testid="credits-bar">
      <ProgressBarTrack>
        <ProgressBarFill $percent={credits.percentage} />
      </ProgressBarTrack>
      <ProgressBarLabels>
        <ProgressBarLabel>{currentFormatted}</ProgressBarLabel>
        <ProgressBarLabel>{thresholdFormatted}</ProgressBarLabel>
      </ProgressBarLabels>
      <ProgressBarMessage data-testid="credits-bar-progress">
        {t('cart.credits.progressPrefix')}{' '}
        <ProgressBarMessageBold>{remainingFormatted}</ProgressBarMessageBold>{' '}
        {t('cart.credits.progressSuffix')}{' '}
        <ProgressBarMessageBold>{balanceFormatted}</ProgressBarMessageBold>{' '}
        {t('cart.credits.progressLabel')}
      </ProgressBarMessage>
    </ProgressBarContainer>
  );
}
